import { Anchor, Group, Stack, Text, Title, type AnchorProps } from '@mantine/core'
import { createLink } from '@tanstack/react-router'
import { useEffect, type AnchorHTMLAttributes, type ReactNode, type Ref } from 'react'

import { IconChevronLeft } from './icons'

/** Sets the browser tab's title for a page: "<title> · Macrotide". */
export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = `${title} · Macrotide`
  }, [title])
}

function BackAnchor(props: AnchorProps & AnchorHTMLAttributes<HTMLAnchorElement> & { ref?: Ref<HTMLAnchorElement> }) {
  const { children, ...rest } = props
  return (
    <Anchor size="sm" c="dimmed" w="fit-content" {...rest}>
      <Group gap={4} wrap="nowrap">
        <IconChevronLeft size={18} />
        {children}
      </Group>
    </Anchor>
  )
}

/** "‹ Library": the way back from an editor or detail page, typed like any router link. */
export const BackLink = createLink(BackAnchor)

/**
 * The top of every page (UX spec §4.5): an optional back link, the title in sentence case, an optional line under it,
 * and at most a couple of actions that wrap under the title on a narrow screen.
 */
export function PageHeader(props: {
  title: string
  /** Shown instead of [title] as the heading when the tab title should differ (the Food log's day). */
  heading?: ReactNode
  subtitle?: ReactNode
  /** A BackLink. */
  back?: ReactNode
  actions?: ReactNode
}) {
  useDocumentTitle(props.title)
  return (
    <Stack gap={4}>
      {props.back}
      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Title order={1}>{props.heading ?? props.title}</Title>
        {props.actions && (
          <Group gap="xs" wrap="wrap">
            {props.actions}
          </Group>
        )}
      </Group>
      {props.subtitle && (
        <Text size="sm" c="dimmed" component="div">
          {props.subtitle}
        </Text>
      )}
    </Stack>
  )
}
